import { useParams, Link } from 'react-router-dom';
import { useEffect } from 'react';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';
import SEO from '@/components/feature/SEO';
import { articles, comments as allComments, categories } from '@/mocks/articles';

export default function Article() {
  const { slug } = useParams<{ slug: string }>();
  const article = articles.find((a) => a.slug === slug);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [slug]);

  if (!article) {
    return (
      <>
        <Navbar />
        <main className="min-h-screen flex items-center justify-center bg-background-50">
          <div className="text-center">
            <h1 className="font-heading text-2xl font-bold text-foreground-900">文章不存在</h1>
            <Link to="/" className="mt-4 inline-block text-primary-500 hover:text-primary-600 text-sm">返回首页</Link>
          </div>
        </main>
        <Footer />
      </>
    );
  }

  const category = categories.find((c) => c.id === article.category);
  const articleComments = allComments.filter((c) => c.articleId === article.id);
  const relatedArticles = articles.filter((a) => a.category === article.category && a.id !== article.id).slice(0, 3);

  const articleSchema = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: article.title,
    description: article.excerpt,
    author: { '@type': 'Person', name: article.author },
    datePublished: article.date,
    url: `http://baoliaowang.cn/article/${article.slug}`,
    publisher: {
      '@type': 'Organization',
      name: '911爆料网',
    },
  };

  const breadcrumbSchema = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: '首页', item: 'http://baoliaowang.cn/' },
      ...(category ? [{ '@type': 'ListItem' as const, position: 2, name: category.name, item: `http://baoliaowang.cn/category/${category.slug}` }] : []),
      { '@type': 'ListItem', position: category ? 3 : 2, name: article.title },
    ],
  };

  return (
    <>
      <SEO
        title={article.title}
        description={article.excerpt}
        keywords={article.tags.join(',') + ',911爆料网,吃瓜黑料,爆料'}
        canonicalUrl={`http://baoliaowang.cn/article/${article.slug}`}
        ogType="article"
        articlePublishedTime={article.date}
        articleAuthor={article.author}
        schema={{ ...articleSchema, ...breadcrumbSchema }}
      />
      <Navbar />
      <main>
        <section className="bg-background-50 pt-24 pb-10 md:pb-14">
          <div className="max-w-4xl mx-auto px-4 md:px-6">
            <Breadcrumb
              items={[
                { label: '首页', href: '/' },
                ...(category ? [{ label: category.name, href: `/category/${category.slug}` }] : []),
                { label: '正文' },
              ]}
            />

            <h1 className="font-heading text-2xl md:text-4xl font-black text-foreground-950 text-center mt-8 leading-tight">
              {article.title}
            </h1>

            <div className="flex items-center justify-center gap-4 mt-5 text-sm text-foreground-500">
              <span>{article.author}</span>
              <span>·</span>
              <span>{article.date} {article.time}</span>
              <span>·</span>
              <span>{article.views.toLocaleString()} 阅读</span>
            </div>

            <div className="flex flex-wrap justify-center gap-2 mt-5">
              {article.tags.map((tag) => (
                <span key={tag} className="px-3 py-1 bg-background-100 text-foreground-600 text-xs rounded-full whitespace-nowrap">
                  #{tag}
                </span>
              ))}
              {article.isExclusive && (
                <span className="px-3 py-1 bg-accent-500 text-background-50 text-xs font-semibold rounded-full whitespace-nowrap">
                  独家
                </span>
              )}
            </div>
          </div>
        </section>

        <section className="bg-background-50 pb-8">
          <div className="max-w-4xl mx-auto px-4 md:px-6">
            <div className="relative w-full max-w-3xl mx-auto h-[320px] md:h-[420px]">
              {article.evidenceImages && article.evidenceImages.length >= 3 && (
                <>
                  <div className="absolute left-[5%] top-8 z-10 w-[200px] md:w-[260px] h-[260px] md:h-[340px] rounded-xl overflow-hidden -rotate-3">
                    <img src={article.evidenceImages[0]} alt="" className="w-full h-full object-cover object-top" />
                  </div>
                  <div className="absolute left-1/2 -translate-x-1/2 top-0 z-20 w-[220px] md:w-[300px] h-[300px] md:h-[400px] rounded-xl overflow-hidden">
                    <img src={article.imageUrl} alt={article.title} title={`911爆料网 - ${article.title}`} className="w-full h-full object-cover object-top" />
                  </div>
                  <div className="absolute right-[5%] top-8 z-10 w-[200px] md:w-[260px] h-[260px] md:h-[340px] rounded-xl overflow-hidden rotate-3">
                    <img src={article.evidenceImages[2]} alt="" className="w-full h-full object-cover object-top" />
                  </div>
                </>
              )}
            </div>
          </div>
        </section>

        <section className="bg-gradient-to-br from-primary-900 via-primary-800 to-primary-950 py-14 md:py-18">
          <div className="max-w-4xl mx-auto px-4 md:px-6">
            <div className="flex flex-col lg:flex-row items-start gap-8 md:gap-10">
              <div className="lg:w-[65%]">
                <span className="inline-block px-3 py-1 bg-accent-100/20 text-accent-300 text-xs font-semibold rounded-full mb-4 whitespace-nowrap">
                  独家
                </span>
                <h2 className="font-heading text-2xl md:text-3xl font-black text-background-50 leading-tight">
                  核心爆料
                </h2>
                <div className="mt-6 text-background-50/85 text-sm md:text-base leading-relaxed space-y-4">
                  {article.content.split('\n\n').map((paragraph, i) => (
                    <p key={i} className="indent-6">{paragraph.trim()}</p>
                  ))}
                </div>
                <div className="mt-8 flex flex-wrap gap-3">
                  <button type="button" className="px-5 py-2.5 bg-background-50 text-primary-800 text-sm font-semibold rounded-full hover:bg-background-100 transition-colors whitespace-nowrap cursor-pointer">
                    查看时间线
                  </button>
                  <button type="button" className="px-5 py-2.5 border border-background-50/30 text-background-50 text-sm font-semibold rounded-full hover:bg-background-50/10 transition-colors whitespace-nowrap cursor-pointer">
                    分享爆料
                  </button>
                </div>
              </div>
              <div className="lg:w-[35%]">
                <div className="w-full aspect-[3/4] rounded-xl overflow-hidden bg-background-50/10">
                  <img
                    src={article.evidenceImages ? article.evidenceImages[1] : article.imageUrl}
                    alt=""
                    className="w-full h-full object-cover object-top"
                  />
                </div>
                <div className="mt-6 text-center">
                  <span className="font-heading text-5xl md:text-6xl font-black text-background-50">
                    Lv.5
                  </span>
                  <p className="mt-2 text-background-50/60 text-sm">实锤指数</p>
                  <p className="mt-1 text-background-50/40 text-xs leading-relaxed">
                    多方证据交叉验证，信息可信度极高
                  </p>
                </div>
              </div>
            </div>
          </div>
        </section>

        <section className="py-16 md:py-20 bg-background-100">
          <div className="max-w-4xl mx-auto px-4 md:px-6">
            <div className="mb-10 md:mb-14">
              <span className="inline-block px-3 py-1 bg-secondary-100 text-secondary-900 text-xs font-semibold rounded-full mb-3 whitespace-nowrap">
                神评论
              </span>
              <h2 className="font-heading text-2xl md:text-4xl font-black text-foreground-950">
                全网吃瓜群众反应
              </h2>
            </div>

            <div className="columns-1 sm:columns-2 lg:columns-3 gap-5 md:gap-6">
              {articleComments.map((comment) => (
                <div key={comment.id} className="break-inside-avoid mb-5 md:mb-6">
                  <div className="bg-background-50 rounded-xl p-4 md:p-5 transition-all duration-300 hover:translate-y-[-2px]">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-full bg-background-200 shrink-0 overflow-hidden">
                        <img src={comment.avatar} alt={comment.author} className="w-full h-full object-cover" />
                      </div>
                      <div>
                        <h4 className="font-heading font-bold text-foreground-900 text-sm">{comment.author}</h4>
                        <p className="text-foreground-400 text-xs">{comment.time}</p>
                      </div>
                    </div>
                    <p className="mt-3 text-foreground-600 text-sm leading-relaxed">
                      {comment.content}
                    </p>
                    <div className="flex items-center gap-1 mt-3 text-foreground-400 text-xs">
                      <i className="ri-heart-line"></i>
                      <span>{comment.likes.toLocaleString()}</span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {relatedArticles.length > 0 && (
          <section className="py-16 md:py-20 bg-background-50">
            <div className="max-w-7xl mx-auto px-4 md:px-6">
              <div className="text-center mb-12">
                <h2 className="font-heading text-2xl md:text-3xl font-black text-foreground-950">
                  相关爆料
                </h2>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                {relatedArticles.map((rel) => (
                  <Link
                    key={rel.id}
                    to={`/article/${rel.slug}`}
                    className="group block"
                  >
                    <div className="bg-background-100 rounded-xl overflow-hidden transition-all duration-300 group-hover:translate-y-[-4px]">
                      <div className="aspect-[16/10] overflow-hidden">
                        <img
                          src={rel.trackImageUrl}
                          alt={rel.title}
                          className="w-full h-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
                        />
                      </div>
                      <div className="p-4">
                        <h3 className="font-heading font-bold text-foreground-900 text-sm leading-snug line-clamp-2 group-hover:text-primary-500 transition-colors">
                          {rel.title}
                        </h3>
                        <p className="text-foreground-500 text-xs mt-2 line-clamp-2">{rel.excerpt}</p>
                      </div>
                    </div>
                  </Link>
                ))}
              </div>
            </div>
          </section>
        )}
      </main>
      <Footer />
    </>
  );
}